import { FC } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ServicesContent } from '../services/contentService';

import bydgoszcz2 from '../assets/fotka1home.jpg';
import czarnkowo2 from '../assets/fotka2home.jpg';
import lubasz2 from '../assets/fotka3home.jpg';
import trzcianka2 from '../assets/fotka4home.jpg';

interface ServicesProps {
  content?: ServicesContent;
}

interface ServiceCardProps {
  title: string;
  description: string;
  imageUrl: string;
  icon?: string;
  index: number;
}

const ServiceCard: FC<ServiceCardProps> = ({ title, description, imageUrl, icon, index }) => {
  return (
    <motion.div 
      className="bg-white rounded-lg shadow-lg overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ 
        y: -10, 
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)" 
      }}
    >
      <div className="h-48 bg-gray-200 overflow-hidden">
        <motion.div 
          className="w-full h-full"
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
        >
          <img 
            src={imageUrl} 
            alt={title} 
            className="w-full h-full object-cover"
          />
        </motion.div>
      </div>
      <div className="p-6">
        <div className="flex items-center mb-2">
          {icon && <span className="text-2xl mr-2">{icon}</span>}
          <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
        </div>
        <p className="text-gray-600">{description}</p>
        <motion.button 
          className="mt-4 text-blue-700 font-medium hover:underline"
          whileHover={{ scale: 1.05, x: 5 }}
          whileTap={{ scale: 0.95 }}
        >
          Zobacz więcej
        </motion.button>
      </div>
    </motion.div>
  );
};

const Services: FC<ServicesProps> = ({ content }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Default services if CMS data is not available
  const defaultServices = {
    title: "Nasze Usługi",
    items: [
      {
        title: 'Ogrody zimowe',
        description: 'Specjalizujemy się w projektowaniu i budowie ogrodów zimowych, zabudowie tarasów i balkonów zarówno w systemach ramowych, jak i bezramowych',
        icon: '🌿'
      },
      {
        title: 'Zabudowa balkonów',
        description: 'Kompleksowa zabudowa balkonów zwiększająca przestrzeń użytkową.',
        icon: '🏠'
      },
      {
        title: 'System bezramowy',
        description: 'Innowacyjne systemy bezramowe dla maksymalnej przejrzystości.',
        icon: '✨'
      },
      {
        title: 'Zadaszenia tarasów i daszki',
        description: 'Eleganckie zadaszenia tarasów i daszki chroniące przed warunkami atmosferycznymi.',
        icon: '🪟'
      },
    ]
  };

  const servicesContent = content || defaultServices;
  
  // Default images for services
  const defaultImages = [bydgoszcz2, czarnkowo2, lubasz2, trzcianka2];

  return (
    <section id="services" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4 md:px-8">
        <motion.h2 
          className="text-3xl font-bold text-center mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
          transition={{ duration: 0.7 }}
          ref={ref}
        >
          {servicesContent.title}
        </motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {servicesContent.items.map((service, index) => (
            <ServiceCard 
              key={index} 
              title={service.title}
              description={service.description}
              icon={service.icon}
              imageUrl={defaultImages[index % defaultImages.length]}
              index={index} 
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
